import React from 'react';

/**
 * 예외처리 4개 탭 공통 UI 디자인 시스템 (기준: 보안예외처리 탭).
 * 검색바·인라인 확장 폼 카드(파란 헤더 + fieldset 그룹)·테이블(지브라·배지)·페이징.
 */

// ─── 스타일 상수 ────────────────────────────────────────────────

export const fldStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.45rem 0.75rem',
  borderRadius: '6px',
  border: '1.5px solid var(--border, #d1d5db)',
  background: 'var(--input-bg, #fff)',
  fontSize: '0.875rem',
  color: 'var(--foreground, #111)',
  outline: 'none',
  boxSizing: 'border-box',
};

export const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.3rem',
  fontWeight: 600,
  fontSize: '0.8rem',
  color: 'var(--muted-foreground, #555)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

export const formFieldBox: React.CSSProperties = {
  background: '#fff',
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: '6px',
  padding: '0.625rem 0.75rem',
};

export const exThStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  textAlign: 'left',
  fontWeight: 700,
  fontSize: '0.75rem',
  color: 'var(--muted-foreground, #64748b)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
};

export const exTdStyle: React.CSSProperties = {
  padding: '0.55rem 0.75rem',
  verticalAlign: 'middle',
};

export const searchBarStyle: React.CSSProperties = {
  padding: '0.4rem 0.65rem',
  borderRadius: '6px',
  border: '1.5px solid var(--border, #d1d5db)',
  background: 'var(--input-bg, #fff)',
  fontSize: '0.8rem',
  color: 'var(--foreground, #111)',
};

/** 지브라 행 배경 */
export const exRowBg = (idx: number): string =>
  idx % 2 === 0 ? 'var(--card-bg, #fff)' : 'var(--table-row-alt, #fafafa)';

// ─── 헤더 (제목 + 건수 + 추가 버튼) ─────────────────────────────

export const ExceptionHeader: React.FC<{
  title: string;
  total: number;
  onAdd?: () => void;
  addLabel?: string;
  description?: string;
}> = ({ title, total, onAdd, addLabel = '+ 예외처리 추가', description }) => (
  <>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>{title}</h2>
        <span style={{ fontSize: '0.82rem', color: 'var(--muted-foreground, #888)', fontWeight: 500 }}>총 {total.toLocaleString()}건</span>
      </div>
      {onAdd && (
        <button type="button" onClick={onAdd} style={{ padding: '0.4rem 1rem', borderRadius: '6px', background: 'var(--accent, #2563eb)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
          {addLabel}
        </button>
      )}
    </div>
    {description && (
      <p style={{ color: 'var(--muted-foreground, #888)', marginBottom: '1rem', fontSize: '0.85rem' }}>{description}</p>
    )}
  </>
);

// ─── 검색 바 컨테이너 ───────────────────────────────────────────

export const SearchBar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ background: 'var(--card-bg, #f9fafb)', border: '1.5px solid var(--border, #e5e7eb)', borderRadius: '8px', padding: '0.875rem 1rem', marginBottom: '1.25rem' }}>
    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
      {children}
    </div>
  </div>
);

// ─── 인라인 확장 폼 카드 (파란 헤더 + 본문 + 푸터) ───────────────

export const FormCard: React.FC<{
  title: string;
  onClose: () => void;
  onSave: () => void;
  isSaving?: boolean;
  saveLabel?: string;
  children: React.ReactNode;
}> = ({ title, onClose, onSave, isSaving, saveLabel = '저장', children }) => (
  <div style={{ border: '1.5px solid var(--border, #e2e8f0)', borderRadius: '10px', marginBottom: '1.5rem', background: 'var(--card-bg, #fff)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
    <div style={{ background: 'var(--accent, #2563eb)', padding: '0.85rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>{title}</span>
      <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer', padding: '0.2rem 0.5rem', fontSize: '1rem', lineHeight: 1 }}>×</button>
    </div>
    <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {children}
    </div>
    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', padding: '0.875rem 1.5rem', borderTop: '1.5px solid var(--border, #e2e8f0)', background: 'var(--card-bg, #f8fafc)' }}>
      <button type="button" onClick={onClose} style={{ padding: '0.45rem 1.25rem', borderRadius: '6px', border: '1.5px solid var(--border, #d1d5db)', background: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}>취소</button>
      <button type="button" onClick={onSave} disabled={isSaving} style={{ padding: '0.45rem 1.5rem', borderRadius: '6px', background: 'var(--accent, #2563eb)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, opacity: isSaving ? 0.6 : 1 }}>
        {isSaving ? '저장 중...' : saveLabel}
      </button>
    </div>
  </div>
);

// ─── 폼 fieldset 그룹 ───────────────────────────────────────────

export const Fieldset: React.FC<{
  legend: string;
  accent?: boolean;
  minColWidth?: number;
  columns?: string;
  children: React.ReactNode;
}> = ({ legend, accent, minColWidth = 200, columns, children }) => (
  <fieldset style={{ margin: 0, padding: '1rem 1.1rem', border: `1.5px solid ${accent ? '#bfdbfe' : 'var(--border, #e2e8f0)'}`, borderRadius: '8px', background: accent ? '#eff6ff' : 'var(--card-bg, #f8fafc)' }}>
    <legend style={{ padding: '0 0.5rem', fontSize: '0.75rem', fontWeight: 700, color: accent ? '#2563eb' : 'var(--muted-foreground, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{legend}</legend>
    <div style={{ display: 'grid', gridTemplateColumns: columns ?? `repeat(auto-fill, minmax(${minColWidth}px, 1fr))`, gap: '0.875rem' }}>
      {children}
    </div>
  </fieldset>
);

/** fieldset 내 개별 필드 (라벨 + 입력) */
export const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={formFieldBox}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

// ─── 테이블 셸 (컨테이너 + thead) ───────────────────────────────

export const TableShell: React.FC<{
  headers: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ headers, children, footer }) => (
  <div style={{ border: '1.5px solid var(--border, #e5e7eb)', borderRadius: '8px', overflow: 'hidden' }}>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
        <thead>
          <tr style={{ background: 'var(--table-header-bg, #f1f5f9)', borderBottom: '2px solid var(--border, #e2e8f0)' }}>
            {headers}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
    {footer}
  </div>
);

// ─── 행 관리 버튼 (수정/삭제) ───────────────────────────────────

export const RowActions: React.FC<{ onEdit: () => void; onDelete: () => void }> = ({ onEdit, onDelete }) => (
  <div style={{ display: 'flex', gap: '0.35rem' }}>
    <button type="button" onClick={onEdit} style={{ padding: '2px 8px', fontSize: '0.72rem', borderRadius: '4px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: 'pointer', fontWeight: 500 }}>수정</button>
    <button type="button" onClick={onDelete} style={{ padding: '2px 8px', fontSize: '0.72rem', borderRadius: '4px', border: '1.5px solid #fca5a5', color: '#dc2626', background: 'transparent', cursor: 'pointer', fontWeight: 500 }}>삭제</button>
  </div>
);

// ─── 로딩 / 빈 상태 ─────────────────────────────────────────────

export const LoadingState: React.FC = () => (
  <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--muted-foreground, #888)' }}>불러오는 중...</div>
);

export const EmptyState: React.FC<{ text?: string }> = ({ text = '등록된 예외처리 항목이 없습니다.' }) => (
  <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--muted-foreground, #888)', border: '1.5px dashed var(--border, #e5e7eb)', borderRadius: '8px' }}>
    {text}
  </div>
);

// ─── 페이징 ─────────────────────────────────────────────────────

export const Pagination: React.FC<{ page: number; totalPages: number; onPage: (p: number) => void }> = ({ page, totalPages, onPage }) => {
  if (totalPages <= 1) return null;
  const btn = (label: string, target: number, disabled: boolean) => (
    <button type="button" onClick={() => onPage(target)} disabled={disabled} style={{ padding: '0.3rem 0.6rem', borderRadius: '5px', border: '1.5px solid var(--border, #d1d5db)', background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1, fontSize: '0.8rem' }}>{label}</button>
  );
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', borderTop: '1.5px solid var(--border, #e5e7eb)', background: 'var(--card-bg, #fff)' }}>
      {btn('«', 1, page === 1)}
      {btn('‹', Math.max(1, page - 1), page === 1)}
      <span style={{ fontSize: '0.82rem', fontWeight: 500, padding: '0 0.5rem', color: 'var(--foreground, #111)' }}>{page} / {totalPages}</span>
      {btn('›', Math.min(totalPages, page + 1), page === totalPages)}
      {btn('»', totalPages, page === totalPages)}
    </div>
  );
};

/** 정렬 가능한 th — 클릭 시 정렬 토글 표시 */
export const SortableTh: React.FC<{
  label: string;
  sortKey: string;
  activeKey?: string;
  dir?: 'asc' | 'desc';
  onSort: (k: string) => void;
}> = ({ label, sortKey, activeKey, dir, onSort }) => {
  const active = activeKey === sortKey;
  return (
    <th onClick={() => onSort(sortKey)} style={{ ...exThStyle, cursor: 'pointer', userSelect: 'none', color: active ? 'var(--accent, #2563eb)' : exThStyle.color }}>
      {label} <span style={{ fontSize: '0.7rem', color: active ? 'var(--accent, #2563eb)' : '#bbb' }}>{active ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
};
